/**
 * API Utility Functions
 *
 * Common utility functions for making API calls with authentication
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import { getApiBaseUrl } from './endpoints';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import userPool from '../../auth/UserPool';

// API Response types following standards
export interface APIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  metadata?: Record<string, any>;
}

export interface APIErrorResponse {
  success: false;
  error: string;
  message: string;
  statusCode: number;
}

// Token refresh state management
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Get auth token from localStorage
// Cognito authorizer expects ID token, not access token
// Must match StatusProvider.tsx line 169
function getAuthToken(): string | null {
  return localStorage.getItem('accessToken');
}

/**
 * Decode JWT token and check if it's expired or about to expire
 * Returns true if token is expired or will expire within 5 minutes
 */
function isTokenExpired(token: string, bufferSeconds: number = 300): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }

    // Decode payload (base64url)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    // Check expiration with buffer (exp is in seconds, Date.now() is in milliseconds)
    if (payload.exp) {
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = bufferSeconds * 1000;

      return expirationTime - currentTime <= bufferTime;
    }

    return true;
  } catch (error) {
    console.error('[API Utils] Error decoding token:', error);
    return true;
  }
}

/**
 * Get current Cognito user from user pool
 */
function getCurrentUser(): CognitoUser | null {
  if (!userPool) {
    console.error('[API Utils] User pool not initialized');
    return null;
  }

  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    console.log('[API Utils] No current user in pool');
    return null;
  }

  return cognitoUser;
}

/**
 * Make an authorized API call with JWT token
 * Automatically handles token refresh if token is expired or about to expire
 */
export async function Make_Authorized_API_Call<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  options?: {
    params?: Record<string, any>;
    headers?: Record<string, string>;
    skipTokenRefresh?: boolean; // Internal flag to prevent infinite loops
  }
): Promise<APIResponse<T>> {
  try {
    let token = getAuthToken();
    if (!token) {
      return {
        success: false,
        data: {} as T,
        message: 'No authentication token found'
      };
    }

    // Check if token is expired or about to expire (within 5 minutes)
    // Skip refresh if this is a retry after refresh to prevent infinite loops
    if (!options?.skipTokenRefresh && isTokenExpired(token)) {
      console.log('[API Utils] Token expired or expiring soon, attempting refresh...');

      const refreshSuccess = await refreshAuthToken();

      if (refreshSuccess) {
        // Get the new token after refresh
        token = getAuthToken();
        if (!token) {
          return {
            success: false,
            data: {} as T,
            message: 'Token refresh succeeded but new token not found'
          };
        }

        console.log('[API Utils] Token refreshed successfully, retrying API call...');
        // Retry the API call with the new token
        // Set skipTokenRefresh to prevent infinite loops
        return Make_Authorized_API_Call<T>(
          endpoint,
          method,
          body,
          { ...options, skipTokenRefresh: true }
        );
      } else {
        // Refresh failed - user will be redirected to login by refreshAuthToken
        return {
          success: false,
          data: {} as T,
          message: 'Token refresh failed - please login again'
        };
      }
    }

    // Build query string for GET requests
    let url = `${getApiBaseUrl()}${endpoint}`;
    if (options?.params && Object.keys(options.params).length > 0) {
      const queryString = new URLSearchParams(options.params).toString();
      url += `?${queryString}`;
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options?.headers
      }
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    // Handle non-2xx responses
    if (!response.ok) {
      // Check for 401 Unauthorized - token may have expired during request
      if (response.status === 401 && !options?.skipTokenRefresh) {
        console.log('[API Utils] Received 401 Unauthorized, attempting token refresh...');

        const refreshSuccess = await refreshAuthToken();

        if (refreshSuccess) {
          console.log('[API Utils] Token refreshed after 401, retrying API call...');
          // Retry the API call with the new token
          return Make_Authorized_API_Call<T>(
            endpoint,
            method,
            body,
            { ...options, skipTokenRefresh: true }
          );
        }
      }

      const errorData = await response.json().catch(() => ({
        error: 'Request failed',
        message: `HTTP ${response.status}: ${response.statusText}`,
        statusCode: response.status
      }));

      return {
        success: false,
        data: {} as T,
        message: errorData.message || errorData.error
      };
    }

    // Parse successful response
    const responseData = await response.json();

    // API responses should follow the Records array pattern
    return {
      success: true,
      data: responseData,
      message: responseData.message,
      metadata: responseData.metadata
    };

  } catch (error) {
    console.error('API call error:', error);
    return {
      success: false,
      data: {} as T,
      message: error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
}

/**
 * Make an unauthenticated API call (for login, etc)
 */
export async function Make_API_Call<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  options?: {
    params?: Record<string, any>;
    headers?: Record<string, string>;
  }
): Promise<APIResponse<T>> {
  try {
    // Build query string for GET requests
    let url = `${getApiBaseUrl()}${endpoint}`;
    if (options?.params && Object.keys(options.params).length > 0) {
      const queryString = new URLSearchParams(options.params).toString();
      url += `?${queryString}`;
    }

    // Prepare fetch options
    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Request failed',
        message: `HTTP ${response.status}: ${response.statusText}`,
        statusCode: response.status
      }));

      return {
        success: false,
        data: {} as T,
        message: errorData.message || errorData.error
      };
    }

    // Parse successful response
    const responseData = await response.json();

    return {
      success: true,
      data: responseData,
      message: responseData.message,
      metadata: responseData.metadata
    };

  } catch (error) {
    console.error('API call error:', error);
    return {
      success: false,
      data: {} as T,
      message: error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
}

/**
 * Handle API errors consistently
 */
export function handleApiError(error: any): string {
  if (error.message) {
    return error.message;
  }
  if (error.error) {
    return error.error;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

/**
 * Refresh authentication token using Cognito
 *
 * Uses amazon-cognito-identity-js SDK to refresh expired access tokens
 * Implements concurrency control to prevent multiple simultaneous refresh attempts
 *
 * @returns Promise<boolean> - true if refresh successful, false otherwise
 */
export async function refreshAuthToken(): Promise<boolean> {
  // If already refreshing, return the existing promise to prevent concurrent refreshes
  if (isRefreshing && refreshPromise) {
    console.log('[API Utils] Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  // Set refreshing flag and create new promise
  isRefreshing = true;
  refreshPromise = new Promise<boolean>(async (resolve) => {
    try {
      console.log('[API Utils] Starting token refresh...');

      // Get current Cognito user
      const cognitoUser = getCurrentUser();
      if (!cognitoUser) {
        console.error('[API Utils] No Cognito user available for refresh');
        resolve(false);
        return;
      }

      // Get current session (includes refresh token)
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          console.error('[API Utils] Error getting session for refresh:', err);
          resolve(false);
          return;
        }

        if (!session) {
          console.error('[API Utils] No session available for refresh');
          resolve(false);
          return;
        }

        // Check if session is valid
        if (!session.isValid()) {
          console.log('[API Utils] Session is invalid, attempting to refresh...');

          // Use the refresh token to get new tokens
          const refreshToken = session.getRefreshToken();

          cognitoUser.refreshSession(refreshToken, (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
            if (refreshErr) {
              console.error('[API Utils] Token refresh failed:', refreshErr);

              // Clear tokens if refresh fails - user needs to re-authenticate
              localStorage.removeItem('accessToken');
              localStorage.removeItem('idToken');
              localStorage.removeItem('user');

              // Redirect to login
              window.location.href = '/login';

              resolve(false);
              return;
            }

            if (!newSession) {
              console.error('[API Utils] No new session returned from refresh');
              resolve(false);
              return;
            }

            // Update stored tokens with new ones
            const newAccessToken = newSession.getAccessToken().getJwtToken();
            const newIdToken = newSession.getIdToken().getJwtToken();

            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('idToken', newIdToken);

            console.log('[API Utils] Token refresh successful');
            resolve(true);
          });
        } else {
          // Session is still valid, just update the tokens
          const accessToken = session.getAccessToken().getJwtToken();
          const idToken = session.getIdToken().getJwtToken();

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('idToken', idToken);

          console.log('[API Utils] Session still valid, tokens updated');
          resolve(true);
        }
      });

    } catch (error) {
      console.error('[API Utils] Unexpected error during token refresh:', error);

      // Clear tokens on unexpected error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('user');

      resolve(false);
    } finally {
      // Reset refresh state after a delay to allow for immediate retries if needed
      setTimeout(() => {
        isRefreshing = false;
        refreshPromise = null;
      }, 1000);
    }
  });

  return refreshPromise;
}